import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { CiblePaiement, Paiement } from 'src/app/core/models/finance.model';
import { PaiementService } from 'src/app/core/services/entities/paiement.service';
import { ParametresService } from 'src/app/core/services/entities/parametres.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';
import { FactureService } from 'src/app/core/services/entities/facture.service';
import { ToastService } from './toast.service';
import { ConfirmService } from './confirm.service';
import { MessagesService } from './messages.service';
import { PhoneUtil } from './phone.util';

function isoAuj(dec = 0): string {
  const x = new Date(); x.setDate(x.getDate() - dec);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
}

/**
 * Encaisser une vente / facture ou régler un achat : montant (reste dû par défaut),
 * date, mode, référence ; historique des paiements avec annulation ; reçu WhatsApp.
 */
@Component({
  selector: 'bay-paiement-sheet',
  template: `
    <bay-bottom-sheet [open]="open" [titre]="cible?.type === 'depense' ? 'Payer le fournisseur' : 'Encaisser'"
      [sousTitre]="cible ? cible.libelle : ''" (closed)="fermer()">
      <ng-container *ngIf="cible">
        <div class="bay-pay-resume">
          <div><span class="bay-kicker">Montant</span><span class="bay-amount">{{ cible.montant | fcfa:true }}</span></div>
          <div><span class="bay-kicker">{{ cible.type === 'depense' ? 'Reste à payer' : 'Reste à encaisser' }}</span>
            <span class="bay-amount" [class.bay-neg]="reste > 0">{{ reste | fcfa:true }}</span></div>
        </div>

        <form *ngIf="reste > 0" (ngSubmit)="valider()" novalidate>
          <div class="bay-field">
            <label class="bay-label" for="pay-montant">Montant {{ cible.type === 'depense' ? 'payé' : 'reçu' }}</label>
            <bay-money-input inputId="pay-montant" name="montant" [(ngModel)]="montant" size="lg"
              [invalid]="erreur !== ''" describedBy="pay-err"></bay-money-input>
            <div class="bay-chips mt-2">
              <button type="button" class="bay-chip" [class.active]="montant === reste" (click)="montant = reste">Tout le reste</button>
              <button *ngIf="reste >= 2" type="button" class="bay-chip" [class.active]="montant === moitie" (click)="montant = moitie">La moitié</button>
            </div>
            <p *ngIf="erreur" id="pay-err" class="bay-error" role="alert">{{ erreur }}</p>
          </div>
          <div class="bay-field">
            <label class="bay-label" for="pay-date">Date</label>
            <div class="d-flex flex-wrap gap-2 align-items-center">
              <div class="bay-chips">
                <button type="button" class="bay-chip" [class.active]="date === aujourdhui" (click)="date = aujourdhui">Aujourd'hui</button>
                <button type="button" class="bay-chip" [class.active]="date === hier" (click)="date = hier">Hier</button>
              </div>
              <input id="pay-date" name="date" type="date" class="bay-input" style="max-width:170px" [(ngModel)]="date">
            </div>
          </div>
          <div class="bay-field">
            <p class="bay-label">Mode de paiement</p>
            <bay-mode-paiement name="mode" [(ngModel)]="mode"></bay-mode-paiement>
          </div>
          <div class="bay-field">
            <label class="bay-label" for="pay-ref">Référence <span class="bay-label-opt">(n° de transaction Wave / OM, n° de chèque…)</span></label>
            <input id="pay-ref" name="reference" class="bay-input" [(ngModel)]="reference" autocomplete="off">
          </div>
          <label *ngIf="cible.type === 'revenu'" class="d-flex align-items-center gap-2 mb-2 fw-semibold small">
            <input type="checkbox" name="recu" [(ngModel)]="envoyerRecu"> Envoyer un reçu sur WhatsApp après l'encaissement
          </label>
          <button type="submit" class="bay-btn bay-btn-primary bay-btn-block" [disabled]="enCours || !montant">
            <span *ngIf="enCours" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            {{ cible.type === 'depense' ? 'Enregistrer le paiement' : 'Encaisser' }} {{ montant ? (montant | fcfa:true) : '' }}
          </button>
        </form>
        <p *ngIf="reste === 0" class="bay-empty bay-empty-compact mb-3">
          <span class="material-symbols-outlined bay-pos" aria-hidden="true">task_alt</span> Entièrement réglé.
        </p>

        <section class="mt-4" *ngIf="historique.length">
          <h3 class="bay-section-h mb-2">Paiements <span class="bay-count">{{ actifs }}</span></h3>
          <ul class="list-unstyled mb-0 bay-pay-list">
            <li *ngFor="let p of historique" [class.bay-pay-annule]="p.statut === 'ANNULE'">
              <div class="min-w-0 flex-grow-1">
                <span class="bay-amount">{{ p.montant | fcfa:true }}</span>
                <span class="bay-hint d-block mb-0">{{ p.date | dateFr }} · {{ p.modePaiement }}<ng-container *ngIf="p.reference"> · réf. {{ p.reference }}</ng-container>
                  <ng-container *ngIf="p.statut === 'ANNULE'"> · annulé{{ p.motifAnnulation ? ' (' + p.motifAnnulation + ')' : '' }}</ng-container></span>
              </div>
              <button *ngIf="p.statut !== 'ANNULE'" type="button" class="bay-btn bay-btn-ghost bay-btn-sm" (click)="annuler(p)"
                [attr.aria-label]="'Annuler le paiement de ' + p.montant + ' FCFA'">Annuler</button>
            </li>
          </ul>
        </section>
      </ng-container>
    </bay-bottom-sheet>
  `,
  styles: [`
    .bay-pay-resume { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; background: var(--bay-green-pale); border-radius: 14px; padding: 12px; margin-bottom: 16px; }
    .bay-pay-resume > div { display: flex; flex-direction: column; }
    .bay-pay-resume .bay-amount { font-size: 18px; }
    .bay-pay-list li { display: flex; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid #eef0ea; }
    .bay-pay-annule { opacity: .6; } .bay-pay-annule .bay-amount { text-decoration: line-through; }
  `]
})
export class PaiementSheetComponent implements OnChanges {
  @Input() open = false;
  @Input() cible: CiblePaiement | null = null;
  @Output() closed = new EventEmitter<void>();
  /** Émis après chaque changement (encaissement ou annulation) avec le nouveau reste */
  /** Pas « change » : le change natif des champs internes (date, référence) remonterait jusqu'au parent */
  @Output() paiementChange = new EventEmitter<{ reste: number; paiement?: Paiement }>();

  montant: number | null = null;
  date = isoAuj();
  mode = '';
  reference = '';
  envoyerRecu = false;
  enCours = false;
  erreur = '';
  reste = 0;
  historique: Paiement[] = [];
  readonly aujourdhui = isoAuj();
  readonly hier = isoAuj(1);

  constructor(private paiements: PaiementService, private factures: FactureService, private parametres: ParametresService, private fermeState: FermeStateService,
              private toast: ToastService, private confirm: ConfirmService, private messages: MessagesService) {}

  get moitie(): number { return Math.round(this.reste / 2); }
  get actifs(): number { return this.historique.filter(p => p.statut !== 'ANNULE').length; }

  ngOnChanges(c: SimpleChanges): void {
    if ((c.open || c.cible) && this.open && this.cible) {
      this.reste = this.cible.reste;
      this.montant = this.reste || null;
      this.date = isoAuj();
      this.reference = '';
      this.erreur = '';
      this.envoyerRecu = false;
      this.parametres.get().subscribe(p => {
        if (!this.mode) this.mode = p?.modePaiementDefaut || 'Wave';
        this.envoyerRecu = this.cible?.type === 'revenu' && !!p?.genererRecu && !!this.cible?.telephone;
      });
      this.charger();
    }
  }

  private charger(): void {
    if (!this.cible) return;
    const f = this.cible.type === 'revenu' ? { revenuId: this.cible.id }
      : this.cible.type === 'facture' ? { factureId: this.cible.id } : { depenseId: this.cible.id };
    this.paiements.lister(this.fermeState.fermeId, f).subscribe({ next: l => this.historique = l, error: () => this.historique = [] });
  }

  valider(): void {
    if (!this.cible || this.enCours) return;
    this.erreur = '';
    if (!this.montant || this.montant <= 0) { this.erreur = 'Indiquez le montant.'; return; }
    if (this.montant > this.reste) { this.erreur = `Le reste dû n'est que de ${this.reste.toLocaleString('fr-FR')} FCFA.`; return; }
    this.enCours = true;
    const c = this.cible;
    const paiement = {
      fermeId: this.fermeState.fermeId, montant: this.montant, date: this.date, modePaiement: this.mode,
      reference: this.reference || undefined,
      revenuId: c.type === 'revenu' ? c.id : undefined, depenseId: c.type === 'depense' ? c.id : undefined, factureId: c.factureId,
    };
    (c.type === 'facture' ? this.factures.encaisser(c.id, paiement, c.creerRevenu !== false) : this.paiements.enregistrer(paiement)).pipe(finalize(() => this.enCours = false)).subscribe({
      next: p => {
        this.reste = p.resteApres ?? Math.max(0, this.reste - p.montant);
        this.historique = [p, ...this.historique];
        this.paiementChange.emit({ reste: this.reste, paiement: p });
        this.toast.succes(`${c.type === 'depense' ? 'Paiement enregistré' : 'Encaissement enregistré'} : ${p.montant.toLocaleString('fr-FR')} FCFA`
          + (this.reste > 0 ? ` · reste ${this.reste.toLocaleString('fr-FR')} FCFA` : ' · soldé'),
          { actions: [{ label: 'Annuler', action: () => this.annulerSansConfirmer(p) }] });
        if (this.envoyerRecu && p.id) {
          // Fenêtre ouverte tout de suite (sinon bloquée), remplie quand le reçu PDF est archivé et partagé
          const fenetre = window.open('', '_blank');
          const envoyer = (lien?: string) => this.messages.recu({ client: c.contactNom, montant: p.montant, date: p.date,
            mode: p.modePaiement, objet: c.libelle, reste: this.reste, lien }).subscribe(txt => {
              const url = PhoneUtil.lienWhatsApp(c.telephone, txt);
              if (fenetre) fenetre.location.href = url; else window.open(url, '_blank', 'noopener');
              this.paiements.journaliser({ type: 'RECU', canal: 'WHATSAPP', fermeId: this.fermeState.fermeId, paiementId: p.id,
                contactId: c.contactId, factureId: c.factureId, libelle: c.libelle, destinataire: PhoneUtil.e164(c.telephone) || undefined })
                .subscribe({ error: () => {} });
            });
          this.paiements.archiverRecu(p.id).subscribe({ next: r => envoyer(r.lienPublic), error: () => envoyer() });
        }
        if (this.reste === 0) setTimeout(() => this.fermer(), 600);
        else this.montant = this.reste;
      },
      error: err => this.toast.erreurHttp(err, 'Paiement impossible.')
    });
  }

  async annuler(p: Paiement): Promise<void> {
    const motif = await this.confirm.motif('Annuler ce paiement ?', `${p.montant.toLocaleString('fr-FR')} FCFA du ${p.date} — il restera visible, barré.`, 'Annuler le paiement');
    if (motif === null) return;
    this.annulerSansConfirmer(p, motif);
  }

  private annulerSansConfirmer(p: Paiement, motif?: string): void {
    this.paiements.annuler(p.id!, motif || undefined).subscribe({
      next: a => {
        this.historique = this.historique.map(x => x.id === a.id ? a : x);
        this.reste = this.reste + p.montant;
        this.montant = this.reste;
        this.paiementChange.emit({ reste: this.reste });
        this.toast.info('Paiement annulé');
      },
      error: err => this.toast.erreurHttp(err, 'Annulation impossible.')
    });
  }

  fermer(): void { this.closed.emit(); }
}
